import { getEnvConfig } from '@hbs-us/contracts';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useSession } from '../../auth/useSession.js';
import { apiFetchJson } from '../../lib/apiFetch.js';
import { getAppEnv } from '../../lib/env.js';
import { notificationsQueryKey, unreadCountQueryKey } from './useNotificationsQuery.js';

/**
 * useNotificationSocket — the live realtime layer for the in-app bell (NOTIF-05 / D-15).
 *
 * The ~30s short-poll (useNotificationsQuery) stays the BASELINE; this hook mounts ON TOP and is
 * purely additive: when the socket is up, a delivered notification arrives the instant it is sent
 * (Durable Object + WebSocket); when the socket drops, the bell silently keeps refreshing on the
 * poll — NO "reconnecting" chrome (D-15 automatic fallback). Live vs poll is invisible to the user.
 *
 * Auth (SEC-11 / Pitfall 3 — load-bearing): the browser cannot ride a SameSite=Lax session cookie
 * on a raw cross-subdomain WS handshake, and a raw tenantId/userId on the URL is forgeable. So the
 * hook performs the two-step handshake the api enforces:
 *   1. FIRST request a short-lived SIGNED ticket from POST /api/v1/notifications/ws-ticket (through
 *      apiFetch — credentialed, the api derives the verified identity from the session/bearer). The
 *      hook never puts a raw tenantId/userId on the URL.
 *   2. Open wss://<api>/api/v1/notifications/ws?ticket=<ticket>. The api verifies the ticket and
 *      forwards the verified identity to the per-tenant DO; the ticket is the only thing on the URL.
 *
 * On each live arrival the hook invalidates the notifications + unread-count queries so the bell
 * REFETCHES the server-rendered list IN THE USER'S OWN LOCALE (the broadcast is a tiny signal, not a
 * rendered row — a tenant DO fans the same payload to every socket, so per-recipient localization is
 * done by the refetch, never by the socket; Pitfall 5 / I18N-04). A burst of arrivals is debounced
 * into a single polite announcement the bell renders in ONE aria-live region.
 *
 * Token-driven: the api host is read from @hbs-us/contracts getEnvConfig — zero hardcoded host/hex.
 */

/** A live arrival signal broadcast by the per-tenant DO (the lightweight, locale-neutral payload). */
interface NotificationSignal {
  type: 'notification';
  notificationId: number;
  recipientUserIds: number[];
}

/** Reconnect backoff bounds (quiet — no user-facing chrome; the poll covers the gap). */
const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;
/** Debounce window: collapse a burst of arrivals into one polite announcement. */
const ANNOUNCE_DEBOUNCE_MS = 800;

export interface NotificationSocketState {
  /**
   * The single debounced arrival announcement for the bell's one aria-live="polite" region
   * ("{n} new notifications" / "New notification"). Empty string when there is nothing to announce.
   * Intentionally the ONLY user-visible output — socket up/down is never surfaced as chrome.
   */
  arrivalAnnouncement: string;
}

/** Build the wss:// WS endpoint from the public api host (never hardcoded). */
function getWsUrl(ticket: string): string {
  const apiHost = getEnvConfig(getAppEnv()).domains.api;
  return `wss://${apiHost}/api/v1/notifications/ws?ticket=${encodeURIComponent(ticket)}`;
}

export function useNotificationSocket(
  announce: (count: number) => string,
): NotificationSocketState {
  const qc = useQueryClient();
  const { status, session } = useSession();
  const userId = session?.user.id ?? null;

  const [arrivalAnnouncement, setArrivalAnnouncement] = useState('');

  // Refs that must survive reconnects without re-subscribing effects.
  const socketRef = useRef<WebSocket | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const announceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArrivalsRef = useRef(0);
  const closedByUnmountRef = useRef(false);

  // Flush the debounced arrival count into ONE polite announcement (bursts collapse to "{n} new").
  const flushAnnouncement = useCallback(() => {
    const count = pendingArrivalsRef.current;
    pendingArrivalsRef.current = 0;
    if (count > 0) setArrivalAnnouncement(announce(count));
  }, [announce]);

  // Handle one live arrival: only react to notifications addressed to THIS user (the DO fans the
  // same payload to every socket of the tenant). Refetch (server renders in the user's locale) +
  // queue a debounced announcement. We never render the socket payload directly (no locale leak).
  const onArrival = useCallback(
    (signal: NotificationSignal) => {
      if (userId === null) return;
      if (!signal.recipientUserIds.includes(userId)) return;

      // Refetch the bell list + unread count — server truth, rendered in this user's locale.
      void qc.invalidateQueries({ queryKey: notificationsQueryKey });
      void qc.invalidateQueries({ queryKey: unreadCountQueryKey });

      pendingArrivalsRef.current += 1;
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
      announceTimerRef.current = setTimeout(flushAnnouncement, ANNOUNCE_DEBOUNCE_MS);
    },
    [qc, userId, flushAnnouncement],
  );

  useEffect(() => {
    // Only run a live socket for an authenticated principal; otherwise the poll baseline alone runs.
    if (status !== 'authenticated' || userId === null) return;
    closedByUnmountRef.current = false;

    let cancelled = false;

    const scheduleReconnect = () => {
      if (closedByUnmountRef.current || cancelled) return;
      // Quiet exponential backoff with a cap — NO chrome. The ~30s poll keeps the bell fresh
      // meanwhile, so a dropped socket is invisible to the user (D-15 fallback).
      const attempt = reconnectAttemptsRef.current;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_MAX_MS);
      reconnectAttemptsRef.current = attempt + 1;
      reconnectTimerRef.current = setTimeout(connect, delay);
    };

    async function connect(): Promise<void> {
      if (cancelled || closedByUnmountRef.current) return;
      try {
        // Step 1: request the signed ticket FIRST (credentialed; no raw identity on the URL).
        const { ticket } = await apiFetchJson<{ ok: boolean; ticket?: string }>(
          '/api/v1/notifications/ws-ticket',
          { method: 'POST', body: JSON.stringify({}) },
        );
        if (cancelled || closedByUnmountRef.current || !ticket) {
          if (!ticket) scheduleReconnect();
          return;
        }

        // Step 2: open the socket carrying ONLY the ticket on the URL.
        const ws = new WebSocket(getWsUrl(ticket));
        socketRef.current = ws;

        ws.addEventListener('open', () => {
          reconnectAttemptsRef.current = 0; // healthy connection resets the backoff
        });

        ws.addEventListener('message', (event) => {
          try {
            const data = JSON.parse(String(event.data)) as Partial<NotificationSignal>;
            if (data.type === 'notification' && Array.isArray(data.recipientUserIds)) {
              onArrival({
                type: 'notification',
                notificationId: Number(data.notificationId),
                recipientUserIds: data.recipientUserIds.filter((n): n is number =>
                  Number.isInteger(n),
                ),
              });
            }
          } catch {
            // A malformed frame is ignored — never throws into the UI; the poll remains the floor.
          }
        });

        // Drop / error → reconnect quietly. No "reconnecting" banner; the poll silently covers.
        ws.addEventListener('close', scheduleReconnect);
        ws.addEventListener('error', () => {
          try {
            ws.close();
          } catch {
            // already closing
          }
        });
      } catch {
        // Ticket mint failed (e.g. transient 401 mid-refresh) → back off and retry; poll covers.
        scheduleReconnect();
      }
    }

    void connect();

    return () => {
      cancelled = true;
      closedByUnmountRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (announceTimerRef.current) clearTimeout(announceTimerRef.current);
      const ws = socketRef.current;
      socketRef.current = null;
      if (ws) {
        try {
          ws.close();
        } catch {
          // already closed
        }
      }
    };
  }, [status, userId, onArrival]);

  return { arrivalAnnouncement };
}
